import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";

import { PRODUCT_IDS, PRODUCT_TYPE_IDS } from "@chef/constants";
import { getCutoffDateForWeek, isOneSubProduct } from "@chef/helpers";

import { setDeviation } from "../features";

import {
  usePickAndMixQuery,
  useProductsByIdsQuery,
} from "../graphql/generated";

import { filterPickAndMix } from "../helpers";
import { getNewBasketDeviation } from "../helpers/basket";
import { usePickAndMixPrice } from "./usePickAndMixPrice";
import { useTrack } from "./useTrack";

export const useUpdateBasketPickAndMixDeviation = (args: {
  week: number;
  year: number;
  skip?: boolean;
}) => {
  const dispatch = useDispatch();

  const track = useTrack();

  const { week, year } = args;

  const skip = !week || !year || args.skip;

  const { getPriceOfPickAndMixProduct, ...pickAndMixPrice } =
    usePickAndMixPrice(args);

  const { data: { products } = {}, ...productsByIdsQuery } =
    useProductsByIdsQuery(
      {
        productIds: [PRODUCT_IDS.ONESUB],
        week,
        year,
      },
      { skip },
    );

  const { data: { pickAndMix } = {}, ...pickAndMixQuery } = usePickAndMixQuery(
    {
      productTypeId: PRODUCT_TYPE_IDS.PICKANDMIX,
      week,
      year,
    },
    { skip },
  );

  const filteredPickAndMix = useMemo(() => {
    if (!pickAndMix) {
      return null;
    }

    return filterPickAndMix({
      pickAndMix,
      cutoffDate: getCutoffDateForWeek({ week, year }),
    });
  }, [pickAndMix]);

  const updateBasketPickAndMixDeviation = useCallback(
    (args: {
      basketProducts: Parameters<
        typeof getNewBasketDeviation
      >[0]["basketProducts"];

      portions: number;

      updateProduct?: Parameters<
        typeof getNewBasketDeviation
      >[0]["updateProduct"];

      manualOverride?: boolean;
    }) => {
      if (!filteredPickAndMix || !products) {
        return false;
      }

      const onesubProduct = products.find((p) => isOneSubProduct(p));

      // This should never happen
      if (!onesubProduct) {
        return false;
      }

      const { newDeviation, updates } = getNewBasketDeviation({
        onesubProduct,
        pickAndMix: filteredPickAndMix,
        getPriceOfPickAndMixProduct,
        ...args,
      });

      if (!newDeviation) {
        return false;
      }

      for (const update of updates) {
        if (update.type === "added") {
          track("recipeAdded", {
            affiliation: "Frontend process",
            recipeId: update.args.recipeId,
            mainRecipeId: update.args.mainRecipeId,
            recipeName: update.args.recipeName,
            recommendationScore: update.args.recommendationScore,
            sortingPosition: update.args.sortingPosition,
            price: update.args.price,
            week,
            year,
          });
        }

        if (update.type === "removed") {
          track("recipeRemoved", {
            affiliation: "Frontend process",
            recipeId: update.args.recipeId,
            mainRecipeId: update.args.mainRecipeId,
            recipeName: update.args.recipeName,
            recommendationScore: update.args.recommendationScore,
            sortingPosition: update.args.sortingPosition,
            price: update.args.price,
            week,
            year,
          });
        }
      }

      dispatch(
        setDeviation({
          basket: { products: newDeviation, week, year },
          manual:
            args.manualOverride === undefined ? true : args.manualOverride,
        }),
      );

      return true;
    },
    [
      dispatch,
      getPriceOfPickAndMixProduct,
      filteredPickAndMix,
      products,
      week,
      year,
    ],
  );

  return {
    updateBasketPickAndMixDeviation,
    isLoading:
      pickAndMixPrice.isLoading ||
      pickAndMixQuery.isLoading ||
      productsByIdsQuery.isLoading,
    isFetching:
      pickAndMixPrice.isFetching ||
      pickAndMixQuery.isFetching ||
      productsByIdsQuery.isFetching,
    isError:
      pickAndMixPrice.isError ||
      pickAndMixQuery.isError ||
      productsByIdsQuery.isError,
  };
};
